import { TextField, TextFieldProps } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined
  onValueChange: (value: number | null) => void
  maximumFractionDigits?: number
  locale?: string
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getLocaleSeparators(locale?: string) {
  const parts = new Intl.NumberFormat(locale).formatToParts(1000.1)
  return {
    group: parts.find(p => p.type === 'group')?.value ?? ',',
    decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
  }
}

function formatLocalisedInput(raw: string, locale?: string) {
  const input = raw
    .replace(/[\u00A0\s]/g, '')
    .replace(/'/g, '')
    .replace(/-/g, '')

  if (!input) return ''

  const { group, decimal } = getLocaleSeparators(locale)
  const otherDecimal = decimal === ',' ? '.' : ','

  const lastDot = input.lastIndexOf('.')
  const lastComma = input.lastIndexOf(',')
  const hasDot = lastDot !== -1
  const hasComma = lastComma !== -1
  const usesLocaleDecimal = input.includes(decimal)
  const hasAnyDecimalCandidate = usesLocaleDecimal || hasDot || hasComma

  const splitIndex = (() => {
    if (usesLocaleDecimal) return input.lastIndexOf(decimal)
    if (hasDot && hasComma) return Math.max(lastDot, lastComma)

    const idx = input.lastIndexOf(otherDecimal)
    if (idx === -1) return -1

    // If the "other" separator looks like a decimal (e.g. 12.34 in a comma-decimal locale),
    // treat it as decimal; otherwise treat as grouping.
    if (input.lastIndexOf(otherDecimal) !== input.indexOf(otherDecimal)) return -1
    const fracLen = input.length - idx - 1
    return fracLen > 0 && fracLen <= 2 ? idx : -1
  })()

  const rawInteger = splitIndex >= 0 ? input.slice(0, splitIndex) : input
  const rawFraction = splitIndex >= 0 ? input.slice(splitIndex + 1) : ''
  const fractionDigits = rawFraction.replace(/[^\d]/g, '')

  const integerDigits = rawInteger.replace(new RegExp(escapeRegExp(group), 'g'), '').replace(/[^\d]/g, '')
  const integerFormatted = integerDigits
    ? new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }).format(Number(integerDigits))
    : ''

  const endsWithSeparator = splitIndex >= 0 && splitIndex === input.length - 1
  if (!hasAnyDecimalCandidate || splitIndex === -1) return integerFormatted
  if (endsWithSeparator) return `${integerFormatted}${decimal}`
  return fractionDigits ? `${integerFormatted}${decimal}${fractionDigits}` : `${integerFormatted}${decimal}`
}

function parseLocalisedNumber(raw: string, locale?: string): number | null {
  const input = raw
    .trim()
    .replace(/[\u00A0\s]/g, '')
    .replace(/'/g, '')

  if (!input) return null

  const { group, decimal } = getLocaleSeparators(locale)
  const otherDecimal = decimal === ',' ? '.' : ','
  const hasDot = input.includes('.')
  const hasComma = input.includes(',')

  let normalized = input.replace(/-/g, '')

  if (hasDot && hasComma) {
    const lastDot = normalized.lastIndexOf('.')
    const lastComma = normalized.lastIndexOf(',')
    const decimalSep = lastDot > lastComma ? '.' : ','
    const otherSep = decimalSep === '.' ? ',' : '.'

    normalized = normalized.replace(new RegExp(`\\${otherSep}`, 'g'), '')
    const idx = normalized.lastIndexOf(decimalSep)
    if (idx >= 0) normalized = `${normalized.slice(0, idx)}.${normalized.slice(idx + 1)}`
  } else {
    const decimalChar = (() => {
      if (normalized.includes(decimal)) return decimal
      if (!normalized.includes(otherDecimal)) return null
      const first = normalized.indexOf(otherDecimal)
      const last = normalized.lastIndexOf(otherDecimal)
      const fracLen = normalized.length - last - 1
      const looksDecimal = first === last && fracLen > 0 && fracLen <= 2
      return looksDecimal ? otherDecimal : null
    })()

    if (decimalChar) {
      const idx = normalized.lastIndexOf(decimalChar)
      const intPart = normalized.slice(0, idx)
      const fracPart = normalized.slice(idx + 1)
      const intDigits = group ? intPart.replace(new RegExp(escapeRegExp(group), 'g'), '') : intPart
      normalized = `${intDigits.replace(/[^\d]/g, '')}.${fracPart.replace(/[^\d]/g, '')}`
    } else {
      if (group) normalized = normalized.replace(new RegExp(escapeRegExp(group), 'g'), '')
      normalized = normalized.replace(/[.,]/g, '')
    }
  }

  normalized = normalized.replace(/[^\d.]/g, '')

  // Keep only the first decimal point, if any.
  const firstDot = normalized.indexOf('.')
  if (firstDot !== -1)
    normalized = normalized.slice(0, firstDot + 1) + normalized.slice(firstDot + 1).replace(/\./g, '')

  if (!normalized || normalized === '.') return null
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

export default function LocalisedNumberTextField({
  value,
  onValueChange,
  maximumFractionDigits = 2,
  locale,
  onBlur,
  onFocus,
  inputRef: externalInputRef,
  inputProps,
  ...props
}: Props) {
  const displayFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits, minimumFractionDigits: 0 }),
    [locale, maximumFractionDigits],
  )

  const [focused, setFocused] = useState(false)
  const [displayValue, setDisplayValue] = useState('')
  const inputElRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (focused) return
    setDisplayValue(typeof value === 'number' ? displayFormatter.format(value) : '')
  }, [displayFormatter, focused, value])

  const mergedInputRef = useCallback(
    (instance: HTMLInputElement | null) => {
      inputElRef.current = instance
      if (!externalInputRef) return
      if (typeof externalInputRef === 'function') externalInputRef(instance)
      else (externalInputRef as any).current = instance
    },
    [externalInputRef],
  )

  const handleFocus: NonNullable<TextFieldProps['onFocus']> = e => {
    setFocused(true)
    setDisplayValue(typeof value === 'number' ? displayFormatter.format(value) : '')
    onFocus?.(e)
  }

  const handleBlur: NonNullable<TextFieldProps['onBlur']> = e => {
    setFocused(false)
    setDisplayValue(typeof value === 'number' ? displayFormatter.format(value) : '')
    onBlur?.(e)
  }

  return (
    <TextField
      {...props}
      type="text"
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={e => {
        const nextRaw = e.target.value
        const selectionStart = e.target.selectionStart ?? nextRaw.length
        const digitsBeforeCaret = nextRaw.slice(0, selectionStart).replace(/[^\d]/g, '').length

        const nextValue = parseLocalisedNumber(nextRaw, locale)
        onValueChange(nextValue)

        const nextFormatted = formatLocalisedInput(nextRaw, locale)
        setDisplayValue(nextFormatted)

        Promise.resolve().then(() => {
          const el = inputElRef.current
          if (!el) return
          let seen = 0
          let caret = 0
          for (; caret < nextFormatted.length; caret++) {
            if (/\d/.test(nextFormatted[caret])) seen++
            if (seen >= digitsBeforeCaret) {
              caret++
              break
            }
          }
          el.setSelectionRange(caret, caret)
        })
      }}
      inputRef={mergedInputRef}
      inputProps={{
        inputMode: 'decimal',
        autoComplete: 'off',
        ...inputProps,
      }}
    />
  )
}
